import { z } from "zod";
const Frame = z.strictObject({ x: z.number(), y: z.number(), w: z.number().positive(), h: z.number().positive() });
/** Presentation overlays are versioned separately from navigation. The serialized
 * layout pins floor/slot geometry; incompatibility selects the original composer. */
const Fixed = z.strictObject({ symbol: z.string(), x: z.number(), y: z.number(), facing: z.enum(["left", "right"]).optional() });
export const LivingEnvironmentSchema = z.strictObject({
    id: z.string().min(1), version: z.literal(1), envId: z.string().min(1), layout: z.string().min(1),
    rooms: z.array(z.strictObject({
        room: z.string(), frame: Frame,
        fixed: z.array(Fixed),
        replaceProps: z.array(z.string()), replaceFixtures: z.array(z.string()),
        /** Modular kits retain live props, attach held objects at measured contacts,
         * and keep furniture independent of the occupants. Older pilot kits omit it. */
        modular: z.boolean().optional(),
        props: z.array(z.strictObject({ symbols: z.array(z.string()), slots: z.array(z.string()).optional(), replacement: z.string(), offsetX: z.number().default(0), offsetY: z.number().default(0) })).optional(),
        seats: z.array(z.strictObject({ slot: z.string(), rear: z.string(), front: z.string(), offsetX: z.number(), offsetY: z.number(), facing: z.enum(["left", "right"]) })).optional(),
        crops: z.array(z.strictObject({ id: z.string(), label: z.string(), frame: Frame })).optional(),
        /** Hard Light (ADR 0017): the painted backdrop drawn while an emphasis
         * moment is open, and the Soft Wash fixed pieces it already paints (shell,
         * windows, doors), which are dropped while it shows. Furniture, seats,
         * people and objects stay independent. Declaring it records that the
         * assembled room was reviewed under Hard Light; a room without it, or with
         * any piece missing, always stays Soft Wash. */
        hardLight: z.strictObject({ fixed: z.array(Fixed).min(1), drops: z.array(z.string()).min(1) }).optional(),
        actors: z.array(z.strictObject({
            symbols: z.array(z.string()), poses: z.array(z.string()), slots: z.array(z.string()),
            facings: z.array(z.enum(["left", "right"])), replacement: z.string(),
            instanceIds: z.array(z.string()).optional(), holdingReplacement: z.string().optional(),
            offsetX: z.number().default(0), offsetY: z.number().default(0),
        })),
    })),
});
/** How a prologue hero is staged in Ink Stroke (environments/illustrations/prologue.json).
 * The authored prologue scene keeps the wide geometry; this says which room the
 * picture frames, what is painted there, where someone who stands off-frame in
 * the wide shot stands in the picture, and how dark a night scene is. */
export const PrologueStagingSchema = z.strictObject({
    sceneId: z.string().min(1),
    room: z.string().min(1),
    /** x and width in scene units; `below` is how far the frame extends under the floor line. */
    frame: z.strictObject({ x: z.number(), w: z.number().positive(), h: z.number().positive(), below: z.number().min(0) }),
    fixed: z.array(Fixed).min(1),
    stage: z.array(z.strictObject({ instanceId: z.string().min(1), slot: z.string().min(1), facing: z.enum(["left", "right"]) })),
    /** Ink opacity at the frame's edge and inside the lamp's pool, and the pool's radius. */
    night: z.strictObject({ edge: z.number().min(0).max(0.8), pool: z.number().min(0).max(0.8), radius: z.number().positive() }),
    /** The authored prologue pages this hero illustrates, by case slug and the page's own index. */
    pages: z.array(z.strictObject({ case: z.string().min(1), index: z.number().int().min(1) })).default([]),
});
//# sourceMappingURL=living-environment.js.map